import React from 'react';
import type { Pressable } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { BodyText, Icon, theme, useResponsive } from '@sweetgreen/garnish';

import { useHomeCardStyles } from '../../hooks';

export const HomeScreenCTAContent = (props: HomeCardProps) => {
  const { title, subtitle, palette = 'primary', testID } = props;

  const shouldUseSecondaryPalette = palette === 'secondary';

  const { match } = useResponsive();

  // ─── Card Styles ────────────────────────────────────────────────

  const cardStyles = useHomeCardStyles();
  const cardContainerStyles = [
    styles.card,
    match([styles.cardXS, styles.cardSM, styles.cardMD]),
    shouldUseSecondaryPalette && styles.cardSecondary,
  ];

  // ────────────────────────────────────────────────────────────────

  return (
    <View style={cardContainerStyles} testID={testID}>
      <View style={styles.content}>
        <BodyText
          style={[
            styles.text,
            shouldUseSecondaryPalette && styles.textSecondary,
            cardStyles.titleStyle,
          ]}
          numberOfLines={match([2, 1])}
        >
          {title}
        </BodyText>

        {subtitle ? (
          <BodyText
            style={[
              styles.text,
              shouldUseSecondaryPalette && styles.textSecondary,
              cardStyles.subtitleStyle,
            ]}
            numberOfLines={match([2, 1])}
          >
            {subtitle}
          </BodyText>
        ) : null}
      </View>

      <Icon
        name="IconArrowRight"
        color={
          shouldUseSecondaryPalette ? theme.colors.GREEN_1 : theme.colors.WHITE
        }
        width={match([24, 24, 24, 48])}
        height={match([24, 24, 24, 48])}
      />
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────

const styles = StyleSheet.create({
  card: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: theme.radius.large,
  },
  cardXS: {
    minHeight: 72,
    padding: theme.spacing.medium,
  },
  cardSM: {
    paddingVertical: theme.spacing.medium,
    paddingHorizontal: theme.spacing.large,
  },
  cardMD: {
    minHeight: 100,
    padding: theme.spacing.large,
  },
  content: {
    flex: 1,
    paddingRight: theme.spacing.small,
  },
  text: { color: theme.colors.WHITE },
  cardSecondary: {
    borderColor: theme.colors.GREEN_2,
    borderWidth: 1,
  },
  textSecondary: {
    color: theme.colors.GREEN_1,
  },
});

// ─── Types ────────────────────────────────────────────────────────

type HomeCardProps = {
  readonly title: string;
  readonly subtitle?: string;
  readonly palette?: 'primary' | 'secondary';
} & Pick<React.ComponentProps<typeof Pressable>, 'testID'>;
